import * as React from 'react';

import { Input, type InputProps } from '@/shared/components/ui/input';
import { cn } from '@/shared/lib/cn';
import { formatNumber, toLatinDigits } from '@/shared/utils/formatters';

export interface MoneyInputProps extends Omit<
  InputProps,
  'value' | 'onChange' | 'type' | 'inputMode'
> {
  /** The amount in Toman. `null` means the field is empty. */
  value: number | null;
  onChange: (value: number | null) => void;
}

/**
 * Currency field that shows a grouped Persian-digit amount while keeping a
 * plain `number` in form state.
 *
 * Users type either Latin or Persian digits (both keyboards are common); the
 * separators are presentation only and are stripped before parsing.
 */
export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(function MoneyInput(
  { value, onChange, className, ...props },
  ref,
) {
  const display = value === null || Number.isNaN(value) ? '' : formatNumber(value);

  return (
    <Input
      ref={ref}
      value={display}
      onChange={(event) => {
        const digitsOnly = toLatinDigits(event.target.value).replace(/[^\d]/g, '');
        onChange(digitsOnly === '' ? null : Number(digitsOnly));
      }}
      // `text` rather than `number`: a numeric input cannot hold the grouped
      // display value, and its spinners are meaningless for currency.
      type="text"
      inputMode="numeric"
      dir="ltr"
      endAdornment="تومان"
      className={cn('tabular text-start', className)}
      {...props}
    />
  );
});
